import React, { useEffect, useState, useRef, useMemo } from "react";
import "../../Styles/styles-StatusTracker.css";
import { useSelector, useDispatch } from "react-redux";
import {
    Tooltip,
    Modal, Tag, Spin, Popover,
    Typography
} from "antd";
import { ACWRDot } from './ACWRDot';
import dayjs from 'dayjs';
import { FaPlus } from "react-icons/fa6";
import { GoPencil } from "react-icons/go";
import { GrFlagFill } from "react-icons/gr";
import {
    selectPlayerStatus,
    deletePlayerStatus,
    deleteCustomStatus
} from '../../store/actions/statustracker.actions'
import {
    PlusOutlined,
    LoadingOutlined,
    InfoCircleOutlined,
    ExclamationCircleOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import { StatusUpdater } from "./StatusUpdater";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { getAcwrColor } from "@utils/acwr_utils";

// Pure function — only depends on its arguments, no component state
const buildGPSCriteriaList = (playerACWRMeta) => {
    const numCriteria =
        ('num_over' in playerACWRMeta && playerACWRMeta['num_over'] >= 2) ||
        ('num_under' in playerACWRMeta && playerACWRMeta['num_under'] >= 2);
    const notInjuredInLatest3Dates = 'injured_within_latest_3_date_range' in playerACWRMeta
        ? !playerACWRMeta['injured_within_latest_3_date_range']
        : true;
    const numDaysInChronicRangeCheck = 'chronic_range_min_count_check' in playerACWRMeta
        ? !playerACWRMeta['chronic_range_min_count_check']
        : false;
    const notInjuredInChronicRange = 'injured_within_chronic_range' in playerACWRMeta
        ? !playerACWRMeta['injured_within_chronic_range']
        : true;
    const playerNotOnLeave = 'player_on_leave' in playerACWRMeta
        ? !playerACWRMeta['player_on_leave']
        : true;

    return [
        {
            condition: numCriteria,
            true_text: "2 or more key metrics outside ACWR limits for 3 consecutive training dates.",
            false_text: "ACWR is within limits or less than 2 key metrics are outside limits for 3 consecutive training dates."
        },
        {
            condition: notInjuredInLatest3Dates,
            true_text: "Player is not injured on any of the 3 latest training dates.",
            false_text: "Player is injured on at least one of the 3 latest training dates."
        },
        {
            condition: numDaysInChronicRangeCheck,
            true_text: "Player has atleast 12 days of data in the chronic range.",
            false_text: "Player has less than 12 days of data in the chronic range."
        },
        {
            condition: notInjuredInChronicRange,
            true_text: "Player is not injured in the chronic range.",
            false_text: "Player is injured in the chronic range."
        },
        {
            condition: playerNotOnLeave,
            true_text: "Player is not on leave.",
            false_text: "Player is on leave."
        }
    ];
};

const renderGPSFlagChecklist = (playerACWRMeta) => {
    const criteriaList = buildGPSCriteriaList(playerACWRMeta);
    const all_criteria_met = criteriaList.every(item => item.condition === true);
    const btnColor = all_criteria_met ? '#ff8000' : '#000000';

    const getIcon = (condition) => condition
        ? <CheckCircleOutlined style={{ color: "#cccccc", marginRight: '5px', fontSize: 15 }}/>
        : <ExclamationCircleOutlined style={{ color: "#ff8000", marginRight: '5px', fontSize: 15 }}/>;

    const content = (
        <span>
            <div>
                <Typography.Title level={5}>GPS Flag Checklist</Typography.Title>
                {criteriaList.map((item, index) => (
                    <React.Fragment key={index}>
                        {getIcon(item.condition)}
                        <Typography.Text disabled={item.condition} delete={item.condition}>
                            {item.condition ? item.true_text : item.false_text}
                        </Typography.Text>
                        <br/>
                    </React.Fragment>
                ))}
            </div>
        </span>
    );

    return (
        <div className="flag-helper-container">
            <Popover placement="leftTop" content={content} trigger="click">
                <InfoCircleOutlined style={{fontSize: 20, color: btnColor}}/>
            </Popover>
        </div>
    );
};

const StatusTrackerCellInner = ({ uniqueKey, dateHeaders }) => {
    const dispatch = useDispatch();
    const FLAG_SIZE = '27px';

    const {
        players,
        info: {
            acwr_threshold_config: acwrThresholdConfig,
        },
        metrics,
        misc: { flag_names_by_type }
    } = useSelector(({ team }) => team);

    // P1: Each cell subscribes only to its own data — other cells' updates don't trigger re-renders here
    const cellInfo = useSelector((state) => state.statusTracker.trackerData[uniqueKey]);
    const statuses = useSelector((state) => state.statusTracker.statuses);
    const flagMetadata = useSelector((state) => state.statusTracker.flagMetadata);
    const uniqueKeyCellLoading = useSelector((state) => state.statusTracker.cellLoading[`${uniqueKey}Cell`]);
    const uniqueKeyModalLoading = useSelector((state) => state.statusTracker.modalLoading[`${uniqueKey}Modal`]);

    // P4: Derived inline — uniqueKey never changes for a mounted cell, no state/useEffect needed
    // Guard: uniqueKey can be undefined for one render cycle during month transitions
    const [playerId, dateStr] = uniqueKey?.split('_') ?? [];
    const player = useMemo(
        () => players.find(p => p.player_id === Number(playerId)),
        [players, playerId]
    );

    // P6: Memoized — metrics is team config, changes at most once per session
    const key_metrics = useMemo(
        () => (metrics || []).filter(m => m.is_key_metric).sort((a, b) => a.order - b.order),
        [metrics]
    );

    // Only truly interactive state remains
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [customInputVisible, setCustomInputVisible] = useState(false);
    const [editingStatus, setEditingStatus] = useState(null);

    const inputRef = useRef(null);

    useEffect(() => {
        if (customInputVisible) {
            inputRef.current?.focus();
        }
    }, [customInputVisible]);

    // Helper functions defined before useMemo calls that reference them

    const getACWRTooltip = (isModalText = false) => {
        if (!cellInfo) return null;
        const hasACWRKey = Object.keys(cellInfo).some(key => key.endsWith('_ACWR'));
        if (!hasACWRKey) return null;

        return (
            <>
                <span style={{fontWeight: 'bold'}}> ACWR: </span> <br/>
                {key_metrics.map((metric, i) => {
                    const acwr_key = `${metric.key}_ACWR`;
                    const acwr_value = cellInfo[acwr_key];
                    const color = getAcwrColor(acwr_value, acwrThresholdConfig);
                    return (
                        <span key={`${metric.key}_ACWR${i}`}>
                            {metric.name} : <span style={{color}}>{acwr_value !== undefined ? acwr_value : '-'}</span> <br/>
                        </span>
                    );
                })}
                {isModalText && <br/>}
            </>
        );
    };

    const getInjuryTooltip = (isModalText = false) => {
        const injuries = cellInfo?.injury || [];
        if (injuries.length === 0) return null;

        return (
            <>
                <span style={{fontWeight: 'bold'}}> Injuries: </span> <br/>
                {injuries.map((injury, idx) => (
                    <div key={`${uniqueKey}_Injury_${idx}`}>
                        <p style={{marginBottom: '0px'}}>Type: {injury.injury_type}</p>
                        <p style={{marginBottom: '0px'}}>Location: {injury.injury_location}</p>
                        <p style={{marginBottom: '0px'}}>Start Date: {injury.injury_start_date}</p>
                        <p style={{marginBottom: '0px'}}>Injury Day: {dayjs(cellInfo.date).diff(dayjs(injury.injury_start_date), 'day') + 1}</p>
                        {!isModalText && cellInfo?.injury.length !== idx + 1 && <hr style={{borderTopColor: 'white'}}/>}
                        {isModalText && <br/>}
                    </div>
                ))}
                {isModalText && <br/>}
            </>
        );
    };

    const getFlagTooltip = (isModalText = false) => {
        const flags = cellInfo?.flags;
        if (!flags) return null;

        const FlagsByType = {};
        let GPSFlagInfo = null;
        Object.keys(flags).forEach((flagSlug) => {
            const flagMeta = flags[flagSlug];
            if (flag_names_by_type && Object.keys(flag_names_by_type).length > 0) {
                const flagNameAndType = flag_names_by_type[flagSlug] || "";
                if (flagNameAndType && flagNameAndType.length > 0) {
                    const [flagName, flagType] = flagNameAndType;
                    if (["Forceplate", "Testing"].includes(flagType)) {
                        if (Object.keys(FlagsByType).includes(flagType)) {
                            FlagsByType[flagType].push([flagName, flagMeta?.metrics || []]);
                        } else {
                            FlagsByType[flagType] = [[flagName, flagMeta?.metrics || []]];
                        }
                    } else if (flagType == "User Defined") {
                        FlagsByType[flagType] = [[flagName, flagMeta || []]];
                    } else {
                        if (Object.keys(FlagsByType).includes(flagType)) {
                            FlagsByType[flagType].push(flagName);
                        } else {
                            FlagsByType[flagType] = [flagName];
                        }
                    }
                }
            }
        });

        if (isModalText && isModalOpen) {
            if (flagMetadata && Object.keys(flagMetadata).length > 0) {
                const playerACWRMetaObj = (flagMetadata[dateStr] || []).filter(fm => fm.player_id === player?.player_id);
                const playerACWRMeta = playerACWRMetaObj[0] || {};
                GPSFlagInfo = renderGPSFlagChecklist(playerACWRMeta);
            }
        }

        return (
            <>
                {FlagsByType && Object.entries(FlagsByType).length > 0 && (
                    <>
                        <span style={{fontWeight: 'bold'}}> Flags: </span> {GPSFlagInfo} <br/>
                        {Object.entries(FlagsByType).map(([flagType, flagNames], i) => (
                            <div key={`Flag${uniqueKey}${flagType}${i}`}>
                                {["Forceplate", "Testing"].includes(flagType)
                                    ? flagNames.map(([flagName, flaggedMetrics], j) => (
                                        <span key={`Flag${uniqueKey}${flagType}${flagName}${i}${j}`}>
                                            {isModalText
                                                ? <>{flagName} {flaggedMetrics?.length > 0 ? `(${flaggedMetrics.join(", ")})` : ''} <br/></>
                                                : <>{flagName} ({flaggedMetrics?.length || 0}) <br/></>
                                            }
                                        </span>
                                    ))
                                    : ( flagType === "User Defined" ? (
                                        flagNames.map(([flagName, flaggedDetails], j) => (
                                            flaggedDetails?.length > 0 && (
                                                <div key={`Flag${uniqueKey}${flagType}${flagName}${i}${j}`}>
                                                    {flaggedDetails.map((flag, k) => {
                                                        const tooltipContent = flag.operand_values
                                                            ? Object.entries(flag.operand_values).map(([opKey, opVal]) => (
                                                                <div key={opKey}>{opKey}: {opVal}</div>
                                                            ))
                                                            : null;
                                                        return (
                                                            <span key={`Flag${uniqueKey}${flagType}${flag.user_defined_flag_id}${k}`} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                                <GrFlagFill color={flag.color} />
                                                                {isModalText ? (
                                                                    <Tooltip title={tooltipContent}>
                                                                        <span style={{ cursor: 'default', borderBottom: '1px dotted currentColor' }}>{flag.name}</span>
                                                                    </Tooltip>
                                                                ) : flag.name}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            )
                                        ))
                                    ) :
                                        flagNames.map((flagName, j) => (
                                            <span key={`Flag${uniqueKey}${flagType}${flagName}${i}${j}`}>
                                                {flagName} ({flagType}) <br/>
                                            </span>
                                        ))
                                    )
                                }
                            </div>
                        ))}
                        {isModalText && <br/>}
                    </>
                )}
                {Object.entries(FlagsByType).length === 0 && <>{GPSFlagInfo}</>}
            </>
        );
    };

    // P3: All status-derived values in one memo — only recomputes when cellInfo changes
    const { cellBGColor, selectedTagStatusId, selectedTagBGColor } = useMemo(() => ({
        cellBGColor: (!cellInfo?.status || Object.keys(cellInfo.status).length === 0) ? '#f2f2f2' : cellInfo.status.color,
        selectedTagStatusId: cellInfo?.status?.status_id || cellInfo?.status?.id,
        selectedTagBGColor: cellInfo?.status?.color,
    }), [cellInfo]);

    const flagNum = useMemo(() => {
        if (!cellInfo?.player_data_exists_for_curr_day) return null;
        const flags = cellInfo?.flags || {};
        let count = 0;
        Object.values(flags).forEach((flagValue) => {
            if (Array.isArray(flagValue)) {
                count += flagValue.length;
            } else {
                count += 1;
            }
        });
        return count > 0 ? count : null;
    }, [cellInfo]);

    const ACWRDotContent = useMemo(() => key_metrics.map((metric, idx) => {
        const acwr_key = `${metric.key}_ACWR`;
        const acwr_value = cellInfo?.[acwr_key];
        return (
            <ACWRDot
                key={`ACWRCircle${uniqueKey}${idx}`}
                circleColor={getAcwrColor(acwr_value, acwrThresholdConfig)}
            />
        );
    }), [cellInfo, key_metrics, acwrThresholdConfig]);

    const ACWRDotTooltipTxt = useMemo(() => {
        if (!cellInfo?.player_data_exists_for_curr_day) return null;
        return getACWRTooltip(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cellInfo, key_metrics, acwrThresholdConfig]);

    const flagTooltipTxt = useMemo(() => {
        if (!cellInfo?.flags) return null;
        return getFlagTooltip(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cellInfo, flag_names_by_type]);

    // Event handlers

    const handleCellClick = () => {
        if (cellInfo && Object.keys(cellInfo).length > 0) {
            setIsModalOpen(true);
        }
    };

    const handleCreateStatus = () => setCustomInputVisible(true);

    const handleCancel = () => {
        setIsModalOpen(false);
        setCustomInputVisible(false);
    };

    const selectStatus = (status) => {
        const params = {
            status_id: status.id,
            player_id: player?.player_id,
            date: dateStr,
        };
        if (cellInfo?.status?.status_id === status?.id) {
            dispatch(deletePlayerStatus(uniqueKey, params));
        } else {
            dispatch(selectPlayerStatus(uniqueKey, params));
        }
    };

    const deleteCustStatus = (status) => {
        const canDelete =
            (status?.mapped_to_num_players === 1 && status.player_ids.includes(player?.player_id)) ||
            status?.mapped_to_num_players === 0;

        if (!canDelete) {
            // B3: use audioMessage instead of raw antd notification
            message.warning(
                `Status '${status.name}' is mapped to ${status?.mapped_to_num_players} player(s). Remove all usages before deleting.`
            );
            return;
        }
        confirmStatusDelete(status);
    };

    const confirmStatusDelete = (status) => {
        let start_date, end_date;
        if (dateHeaders && Object.keys(dateHeaders).length > 0) {
            const datesToQuery = Object.keys(dateHeaders);
            start_date = datesToQuery[0];
            end_date = datesToQuery[datesToQuery.length - 1];
        }
        dispatch(deleteCustomStatus(uniqueKey, { status_id: status?.id, start_date, end_date }, status.mapping));
    };

    const handleStatusEditClick = (e, status) => {
        e.stopPropagation();
        if (editingStatus !== null) {
            // B3: use audioMessage instead of raw antd notification
            message.warning('Please complete the current edit before editing another status.');
            return;
        }
        setEditingStatus(status);
    };

    const statusEditorHandler = () => setEditingStatus(null);
    const statusCreatorHandler = () => setCustomInputVisible(false);

    // Null guard: during month transitions dateColumns useMemo updates before
    // trackerDataSource useEffect, producing one render cycle with undefined uniqueKey
    if (!uniqueKey) return null;

    if (uniqueKeyCellLoading) {
        return <Spin indicator={<LoadingOutlined spin />} size="small" />;
    }

    return (
        <>
            <div className="tracker-cell" style={{ backgroundColor: cellBGColor }} onClick={handleCellClick}>
                <div>
                    {/* B2: optional chaining on injury to avoid crash when field is undefined */}
                    {cellInfo?.injury?.length > 0 &&
                        <Tooltip title={getInjuryTooltip()}>
                            <FaPlus style={{color: '#888888'}}/>
                        </Tooltip>
                    }
                    <Tooltip title={flagTooltipTxt}>
                        <div className="tracker-cell-flag-container" style={{ minWidth:{FLAG_SIZE}, minHeight:{FLAG_SIZE} }}>
                            {flagNum > 0 ? (
                                <svg fill="#000000" width={FLAG_SIZE} height={FLAG_SIZE} viewBox="0 0 256 256" id="Flat" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M217.78906,44.42639a4.00624,4.00624,0,0,0-4.18945.37793c-30.15039,22.61426-56.31934,11.39746-84.02441-.47656C101,32.08166,71.4502,19.4176,37.59961,44.80432c-.07227.0542-.12744.12158-.19482.17944a4.0389,4.0389,0,0,0-.31006.2854c-.08936.09522-.167.19629-.2461.29834-.0791.10254-.15771.20411-.227.31446s-.12744.22461-.18506.34033c-.0581.11621-.11523.2312-.16211.35327-.04638.12207-.08056.24609-.11523.37158-.03369.12329-.06836.24536-.09033.37329a4.01251,4.01251,0,0,0-.043.42627c-.00537.08692-.02588.16944-.02588.25782v168a4,4,0,0,0,8,0V170.05579c29.47852-21.042,55.21191-10.0376,82.4248,1.62548,16.43653,7.04395,33.19434,14.22657,50.90821,14.22657,13.082-.001,26.6875-3.91895,41.06738-14.70313A4.00206,4.00206,0,0,0,220,168.00452v-120A4.0023,4.0023,0,0,0,217.78906,44.42639ZM212,165.97522c-29.47852,21.041-55.21289,10.01562-82.4248-1.64746-26.75977-11.468-54.37452-23.29932-85.5752-3.908V50.05579c29.47852-21.042,55.21191-10.0376,82.4248,1.62548,26.76075,11.46778,54.376,23.30078,85.5752,3.91114Z"/>
                                    <text x="50%" y="46%" dominantBaseline="middle" textAnchor="middle" fill="red" fontSize="88px" fontWeight="bold">
                                        {flagNum}
                                    </text>
                                </svg>
                            ) : <></>}
                        </div>
                    </Tooltip>
                </div>
                <Tooltip title={ACWRDotTooltipTxt}>
                    <div className="status-tracker-acwr-dot-container">
                        {ACWRDotContent}
                    </div>
                </Tooltip>
            </div>
            <Modal
                title={`${player?.first_name} ${player?.last_name} - ${dateStr}`}
                open={isModalOpen}
                onCancel={handleCancel}
                footer={null}
            >
                <div>
                    {cellInfo && Object.keys(cellInfo).length > 0 && (
                        <div>
                            {getACWRTooltip(true)}
                            {getFlagTooltip(true)}
                            {getInjuryTooltip(true)}
                        </div>
                    )}
                    <span><strong>Select Status:   </strong></span>
                    {uniqueKeyModalLoading && <Spin indicator={<LoadingOutlined spin />}/>}
                    <br/>
                    <div className="modal-tag">
                        {statuses && statuses.length > 0 && statuses.map((status, i) => (
                            (editingStatus !== null && (editingStatus.id === status.id || editingStatus.status_id === status.id))
                                ? (
                                    <StatusUpdater
                                        key={`${uniqueKey}-modal-tag-${i}`}
                                        uniqueKey={uniqueKey}
                                        status={status}
                                        mode={"edit"}
                                        confirmCancelHandler={statusEditorHandler}
                                        dateHeaders={dateHeaders}
                                    />
                                )
                                : (
                                    <Tag
                                        className="modal-tag-individual"
                                        key={`${uniqueKey}-modal-tag-${i}`}
                                        closable={status.delete_allowed}
                                        style={{
                                            backgroundColor: status.id === selectedTagStatusId ? selectedTagBGColor : status.color,
                                            borderColor: status.id === selectedTagStatusId ? 'black' : status.color,
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => selectStatus(status)}
                                        onClose={(e) => {
                                            e.preventDefault();
                                            deleteCustStatus(status);
                                        }}
                                    >
                                        {status.name}
                                        {status.edit_allowed && (
                                            <span
                                                onClick={e => handleStatusEditClick(e, status)}
                                                style={{ marginBottom: 4, marginLeft: 4, cursor: "pointer" }}
                                            >
                                                <GoPencil />
                                            </span>
                                        )}
                                    </Tag>
                                )
                        ))}
                        {customInputVisible
                            ? (
                                <StatusUpdater
                                    uniqueKey={uniqueKey}
                                    mode={"create"}
                                    confirmCancelHandler={statusCreatorHandler}
                                    dateHeaders={dateHeaders}
                                />
                            )
                            : (
                                <Tag className='modal-tag-individual custom-tag' icon={<PlusOutlined />} onClick={handleCreateStatus}>
                                    New Status
                                </Tag>
                            )
                        }
                    </div>
                </div>
            </Modal>
        </>
    );
};

// React.memo: skips re-render when uniqueKey and dateHeaders props are unchanged.
// Pagination and filter changes rebuild trackerDataSource but don't change individual
// cell props — without memo, all visible cells would re-render on every page turn.
export const StatusTrackerCell = React.memo(StatusTrackerCellInner);
